import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ApiService,
  BillyBillingHistoryMonth,
  BillyConsumptionData,
  BillyConsumptionFilters
} from '../../services/api.service';
import { PermissionsService } from '../../services/permissions.service';

type VisibleBillyConsumption = Extract<BillyConsumptionData, { visible: true }>;
type DateMode = 'period' | 'custom';

interface PeriodOption {
  key: string;
  label: string;
  startDate: string;
  endDate: string;
}

// Una línea de concepto facturable dentro de un agente: «segundos conversación
// · 24.704 s · 143,28 €», en la unidad con la que factura su regla (#588). El texto de unidades ya viene formateado (se calcula
// una vez por respuesta, no en cada ciclo de detección de cambios).
interface BreakdownLine {
  key: string;
  label: string;
  unitsText: string;
  subtotal: number;
}

// Nivel agente: una fila por agente, con sus conceptos facturables dentro.
// `unitsText` es el tiempo total del agente en la unidad de sus reglas
// («24.704 s», o «24.704 s · 90 min» si mezcla reglas por segundo y por
// minuto) y vacío si no tuvo consumo medido en tiempo.
interface BreakdownGroup {
  key: string;
  name: string;
  subtotal: number;
  unitsText: string;
  lines: BreakdownLine[];
  hasDetail: boolean;
}

// Nivel proyecto (cliente final del cliente). Cuando el proyecto tiene un
// único agente no se pinta este nivel: la fila del agente sube tal cual, que
// es como se ve hoy en las cuentas que no trabajan por proyecto.
interface ProjectGroup {
  key: string;
  name: string;
  subtotal: number;
  unitsText: string;
  agents: BreakdownGroup[];
  hasAgentLevel: boolean;
}

type UnitCode = 'seconds' | 'minutes' | 'calls';

// Unidad de las líneas de reglas v1, que el servidor antiguo no etiqueta con
// `unit`. El servidor nuevo la manda siempre; esto solo evita que el panel
// pierda las unidades si el dashboard se despliega antes que el ai-server.
const UNIT_BY_BILLING_TYPE: Record<string, UnitCode> = {
  per_successful_call: 'calls',
  per_closed_call: 'calls',
  per_aht_scale: 'calls',
  per_initiated_minutes: 'minutes',
  per_initiated_minutes_closed: 'minutes',
  per_accumulated_seconds_no_transfer: 'minutes'
};

// OnPush es obligatorio aquí, no una optimización opcional: el panel vive
// fuera del router-outlet (app.component.html) y por tanto sigue montado en
// todas las rutas. Con la estrategia por defecto, cualquier click, timer o
// respuesta HTTP de la página re-evaluaba todo su template — y en la vista de
// admin el desglose tiene una fila por agente, así que en /agents con la lista
// completa eso bastaba para bloquear el hilo principal.
@Component({
  selector: 'app-billy-consumption-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './billy-consumption-panel.component.html',
  styleUrl: './billy-consumption-panel.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BillyConsumptionPanelComponent implements OnInit, OnDestroy {
  @Input() isDarkMode = false;

  loading = false;
  error = false;
  collapsed = false;
  canRender = false;
  consumption: VisibleBillyConsumption | null = null;
  dateMode: DateMode = 'period';
  periodOptions: PeriodOption[] = [];
  selectedPeriodKey = '';
  customStart = '';
  customEnd = '';
  filterError = '';
  filtersCollapsed = false;
  periodDropdownOpen = false;
  showHistoryChart = false;
  historyLoading = false;
  historyError = false;
  historyLoaded = false;
  billingHistory: BillyBillingHistoryMonth[] = [];
  tickerMessages: string[] = [];
  tickerIndex = 0;
  tickerVisible = true;

  private readonly COLLAPSED_KEY = 'meetzy_billy_consumption_panel_collapsed';
  private readonly TICKER_INTERVAL_MS = 4500;
  private readonly TICKER_FADE_MS = 220;
  private historyChart: any = null;
  private tickerTimer: ReturnType<typeof setInterval> | null = null;
  private tickerFadeTimer: ReturnType<typeof setTimeout> | null = null;
  private documentClickListener: ((event: MouseEvent) => void) | null = null;

  constructor(
    private apiService: ApiService,
    private permissionsService: PermissionsService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  async ngOnInit(): Promise<void> {
    this.restoreCollapsedState();

    const canView = await this.permissionsService.canViewBillableMinutes();
    if (!canView) {
      return;
    }

    await this.loadConsumption();
  }

  ngOnDestroy(): void {
    this.destroyHistoryChart();
    this.stopTicker();
    this.detachDocumentClickListener();
  }

  async loadConsumption(filters?: BillyConsumptionFilters): Promise<void> {
    this.loading = true;
    this.error = false;
    this.filterError = '';
    this.consumption = null;
    this.breakdownGroups = [];
    this.cdr.markForCheck();

    try {
      const response = await this.apiService.getCurrentBillyConsumption(filters);
      if (response.success && response.data.visible) {
        this.consumption = response.data;
        this.breakdownGroups = this.buildBreakdownGroups(response.data);
        this.canRender = true;
        this.syncFiltersFromConsumption(response.data);
        if (!this.isAdminView) {
          this.showHistoryChart = false;
          this.historyLoaded = false;
          this.billingHistory = [];
          this.destroyHistoryChart();
        }
        this.refreshTicker();
      } else {
        this.consumption = null;
        this.breakdownGroups = [];
        this.canRender = false;
        this.tickerMessages = [];
        this.stopTicker();
      }
    } catch (err) {
      console.error('Billy consumption panel error:', err);
      this.error = true;
      this.canRender = true;
      this.tickerMessages = [];
      this.stopTicker();
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  async onPeriodChange(periodKey: string): Promise<void> {
    const selectedPeriod = this.periodOptions.find((option) => option.key === periodKey);
    if (!selectedPeriod) {
      return;
    }

    this.dateMode = 'period';
    this.selectedPeriodKey = selectedPeriod.key;
    this.customStart = selectedPeriod.startDate;
    this.customEnd = selectedPeriod.endDate;

    await this.loadConsumption({
      startDate: selectedPeriod.startDate,
      endDate: selectedPeriod.endDate
    });
  }

  async applyCustomRange(): Promise<void> {
    this.dateMode = 'custom';
    if (!this.customStart || !this.customEnd) {
      this.filterError = 'Select a start and end date.';
      return;
    }

    if (this.customStart > this.customEnd) {
      this.filterError = 'Start date cannot be after end date.';
      return;
    }

    await this.loadConsumption({
      startDate: this.customStart,
      endDate: this.customEnd
    });
  }

  async refreshCurrentFilter(): Promise<void> {
    if (this.dateMode === 'custom' && this.customStart && this.customEnd) {
      await this.applyCustomRange();
      return;
    }

    const selectedPeriod = this.periodOptions.find((option) => option.key === this.selectedPeriodKey);
    if (selectedPeriod) {
      await this.onPeriodChange(selectedPeriod.key);
      return;
    }

    await this.loadConsumption();
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    try {
      localStorage.setItem(this.COLLAPSED_KEY, String(this.collapsed));
    } catch {
      // Ignore storage failures in private browsing or restricted contexts.
    }

    if (this.collapsed) {
      this.tickerIndex = 0;
      this.tickerVisible = true;
      this.startTicker();
    } else {
      this.stopTicker();
    }
  }

  toggleFiltersCollapsed(): void {
    this.filtersCollapsed = !this.filtersCollapsed;
    if (this.filtersCollapsed) {
      this.periodDropdownOpen = false;
    }
  }

  togglePeriodDropdown(): void {
    if (this.loading) {
      return;
    }
    if (this.periodDropdownOpen) {
      this.closePeriodDropdown();
    } else {
      this.periodDropdownOpen = true;
      this.attachDocumentClickListener();
    }
  }

  private closePeriodDropdown(): void {
    this.periodDropdownOpen = false;
    this.detachDocumentClickListener();
  }

  async selectPeriod(periodKey: string): Promise<void> {
    this.closePeriodDropdown();
    if (periodKey === this.selectedPeriodKey && this.dateMode === 'period') {
      return;
    }
    await this.onPeriodChange(periodKey);
  }

  get selectedPeriodLabel(): string {
    const match = this.periodOptions.find((option) => option.key === this.selectedPeriodKey);
    return match?.label || this.consumption?.period_label || this.periodOptions[0]?.label || 'Select period';
  }

  // El "click fuera" se escucha sólo mientras el desplegable está abierto y
  // fuera de la zona de Angular. Con @HostListener('document:click') cada
  // click de la página entraba en la zona y marcaba este panel para revisión,
  // aunque el desplegable estuviera cerrado.
  private attachDocumentClickListener(): void {
    if (this.documentClickListener) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.documentClickListener = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('[data-billy-period-dropdown]')) {
          return;
        }
        // Volvemos a la zona sólo cuando hay algo que repintar.
        this.zone.run(() => {
          this.closePeriodDropdown();
          this.cdr.markForCheck();
        });
      };
      document.addEventListener('click', this.documentClickListener);
    });
  }

  private detachDocumentClickListener(): void {
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = null;
    }
  }

  get isAdminView(): boolean {
    return this.consumption?.view_mode === 'admin_clients';
  }

  get showFinancials(): boolean {
    return !!this.consumption;
  }

  // Las filas del desglose se pintan desde un <ng-template>, donde el
  // estrechamiento de tipo de *ngIf="consumption" no llega; con este getter la
  // divisa se lee igual desde dentro y desde fuera.
  get currency(): string {
    return this.consumption?.currency || 'EUR';
  }

  async toggleHistoryChart(): Promise<void> {
    this.showHistoryChart = !this.showHistoryChart;

    if (!this.showHistoryChart) {
      return;
    }

    if (!this.historyLoaded) {
      await this.loadBillingHistory();
      return;
    }

    setTimeout(() => void this.renderHistoryChart());
  }

  async loadBillingHistory(): Promise<void> {
    this.historyLoading = true;
    this.historyError = false;

    try {
      const response = await this.apiService.getBillyBillingHistory();
      if (response.success) {
        this.billingHistory = response.data.months || [];
        this.historyLoaded = true;
        this.refreshTicker();
        setTimeout(() => void this.renderHistoryChart());
      } else {
        this.historyError = true;
      }
    } catch (err) {
      console.error('Billy billing history panel error:', err);
      this.historyError = true;
    } finally {
      this.historyLoading = false;
      this.cdr.markForCheck();
    }
  }

  // Estos formateadores se llaman una vez por fila y por ciclo de detección de
  // cambios; construir un Intl.NumberFormat cada vez es de lo más caro que se
  // puede poner en un template, así que se cachean por divisa.
  private static readonly currencyFormatters = new Map<string, Intl.NumberFormat>();
  private static readonly numberFormatter = new Intl.NumberFormat('es-ES', {
    maximumFractionDigits: 2
  });

  // es-ES no agrupa los números de cuatro cifras («1234»); para las duraciones
  // sí se agrupa, igual que en el correo de uso («1.234 min»). Si el motor de
  // Intl no entiende useGrouping: 'always' cae al comportamiento por defecto.
  // Unidades facturadas: con separador de miles también en cuatro cifras
  // («1.234 s»; es-ES por defecto escribe «1234») y hasta dos decimales, que
  // las reglas por minuto exacto pueden dar minutos fraccionados.
  private static readonly unitsFormatter = new Intl.NumberFormat('es-ES', {
    maximumFractionDigits: 2,
    useGrouping: 'always'
  } as unknown as Intl.NumberFormatOptions);

  formatCurrency(value: number | null | undefined, currency: string = 'EUR'): string {
    let formatter = BillyConsumptionPanelComponent.currencyFormatters.get(currency);
    if (!formatter) {
      formatter = new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      BillyConsumptionPanelComponent.currencyFormatters.set(currency, formatter);
    }
    return formatter.format(Number(value || 0));
  }

  formatNumber(value: number | null | undefined): string {
    return BillyConsumptionPanelComponent.numberFormatter.format(Number(value || 0));
  }

  // Seconds-based balances read better as minutes with the exact seconds as
  // context; other units render as-is.
  formatBalanceUnits(value: number | null | undefined, unit: string): string {
    const units = Number(value || 0);
    if (unit === 'seconds') {
      return `${this.formatNumber(Math.round(units / 60))} min`;
    }
    if (unit === 'calls') {
      return `${this.formatNumber(units)} calls`;
    }
    return `${this.formatNumber(units)} min`;
  }

  // Minutos equivalentes del saldo prepago (al precio de la bolsa), al lado de
  // los euros: «≈ 37.832 min». Los segundos se pasan a minutos redondeados.
  // Barra de la bolsa prepago, en el panel y en la tira siempre visible: lo
  // que queda sobre el tamaño de la bolsa actual. El color avisa cuando se
  // acerca el final (ámbar por debajo del 25 %, rojo por debajo del 10 %).
  get prepaidGauge(): {
    percent: number;
    availableEur: number;
    sizeEur: number;
    tone: 'ok' | 'low' | 'critical';
    validUntil: string | null;
  } | null {
    const balance = this.consumption?.balance;
    if (balance?.kind !== 'prepaid_bag' || balance.available_eur == null) return null;
    if (balance.remaining_percent == null || !balance.bag_size_eur) return null;
    const percent = balance.remaining_percent;
    const validUntilDate = balance.valid_until ? this.parseDateKey(String(balance.valid_until).slice(0, 10)) : null;
    return {
      percent,
      availableEur: balance.available_eur,
      sizeEur: balance.bag_size_eur,
      tone: percent < 10 ? 'critical' : percent < 25 ? 'low' : 'ok',
      validUntil: validUntilDate ? this.formatShortDate(validUntilDate) : null,
    };
  }

  prepaidToneClass(tone: 'ok' | 'low' | 'critical'): string {
    if (tone === 'critical') return 'bg-red-500';
    if (tone === 'low') return 'bg-amber-500';
    return 'bg-emerald-500';
  }

  formatPrepaidUnits(value: number | null | undefined, unit: string): string {
    const units = Number(value || 0);
    if (unit === 'seconds') return this.formatBilledUnits(Math.round(units / 60), 'minutes');
    if (unit === 'minutes') return this.formatBilledUnits(Math.round(units), 'minutes');
    return this.formatBilledUnits(units, null);
  }

  getUnitText(item: { units: number; unit_label: string }): string {
    return `${this.formatNumber(item.units)} ${this.translateUnitLabel(item.unit_label, item.units)}`;
  }

  private restoreCollapsedState(): void {
    try {
      this.collapsed = localStorage.getItem(this.COLLAPSED_KEY) === 'true';
    } catch {
      this.collapsed = false;
    }
  }

  private syncFiltersFromConsumption(consumption: VisibleBillyConsumption): void {
    // Only (re)build the period list from a current-period response (or the very
    // first load). Selecting an older period returns a 'custom' response anchored
    // on that older date; rebuilding from it would drop the more-recent periods
    // (e.g. picking May would hide June). Keeping the list anchored on the current
    // period makes navigation stable in both directions.
    if (this.periodOptions.length === 0 || consumption.period_mode === 'current') {
      this.periodOptions = this.buildPeriodOptions(
        consumption.billing_period_start_day,
        consumption.period_start_date,
        consumption.period_end_date,
        8
      );
    }

    const matchingPeriod = this.periodOptions.find((option) =>
      option.startDate === consumption.period_start_date && option.endDate === consumption.period_end_date
    );

    if (consumption.period_mode === 'custom') {
      this.dateMode = 'custom';
      this.selectedPeriodKey = matchingPeriod?.key || '';
    } else {
      this.dateMode = 'period';
      this.selectedPeriodKey = matchingPeriod?.key || this.periodOptions[0]?.key || '';
    }

    this.customStart = consumption.period_start_date;
    this.customEnd = consumption.period_end_date;
  }

  private buildPeriodOptions(
    billingPeriodStartDay: number,
    anchorDateKey: string,
    currentPeriodEndDateKey: string,
    count: number
  ): PeriodOption[] {
    const anchor = this.parseDateKey(anchorDateKey) || new Date();
    const currentStart = this.getBillingPeriodStart(anchor, billingPeriodStartDay);
    const options: PeriodOption[] = [];

    for (let index = 0; index < count; index++) {
      const start = new Date(Date.UTC(
        currentStart.getUTCFullYear(),
        currentStart.getUTCMonth() - index,
        currentStart.getUTCDate()
      ));
      const nextStart = new Date(Date.UTC(
        start.getUTCFullYear(),
        start.getUTCMonth() + 1,
        start.getUTCDate()
      ));
      const end = index === 0
        ? this.parseDateKey(currentPeriodEndDateKey) || new Date(nextStart.getTime() - 24 * 60 * 60 * 1000)
        : new Date(nextStart.getTime() - 24 * 60 * 60 * 1000);
      const startDate = this.formatDateKey(start);
      const endDate = this.formatDateKey(end);

      options.push({
        key: `${startDate}_${endDate}`,
        label: `${this.formatShortDate(start)} - ${this.formatShortDate(end)}`,
        startDate,
        endDate
      });
    }

    return options;
  }

  private getBillingPeriodStart(date: Date, billingPeriodStartDay: number): Date {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = Math.min(billingPeriodStartDay || 1, this.daysInMonth(year, month));
    const candidate = new Date(Date.UTC(year, month, day));

    if (date < candidate) {
      const previousMonth = month - 1;
      const previousYear = new Date(Date.UTC(year, previousMonth, 1)).getUTCFullYear();
      const normalizedPreviousMonth = new Date(Date.UTC(year, previousMonth, 1)).getUTCMonth();
      const previousDay = Math.min(
        billingPeriodStartDay || 1,
        this.daysInMonth(previousYear, normalizedPreviousMonth)
      );
      return new Date(Date.UTC(previousYear, normalizedPreviousMonth, previousDay));
    }

    return candidate;
  }

  private daysInMonth(year: number, month: number): number {
    return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  }

  private parseDateKey(value: string): Date | null {
    const date = new Date(`${value}T00:00:00.000Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatDateKey(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private formatShortDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(date);
  }

  private translateUnitLabel(label: string, units: number): string {
    const normalized = String(label || '').toLowerCase();
    const translations: Record<string, string> = {
      'llamadas exitosas': units === 1 ? 'successful call' : 'successful calls',
      'llamadas closed': units === 1 ? 'closed call' : 'closed calls',
      'minutos iniciados': units === 1 ? 'initiated minute' : 'initiated minutes',
      'minutos iniciados (closed)': units === 1 ? 'initiated minute (closed)' : 'initiated minutes (closed)',
      'minutos (seg. acum. sin transfer)': units === 1 ? 'minute (no-transfer accumulated seconds)' : 'minutes (no-transfer accumulated seconds)',
      'llamadas (escala aht)': units === 1 ? 'call (AHT scale)' : 'calls (AHT scale)',
      'regla facturable': units === 1 ? 'billable rule' : 'billable rules',
      'reglas facturables': units === 1 ? 'billable rule' : 'billable rules',
      // Rules-v2 unit labels (per-second contracts).
      'segundos conversación': 'conversation seconds',
      'segundos conversacion': 'conversation seconds',
      'buzones': units === 1 ? 'voicemail' : 'voicemails',
      'seg. transfer entrante': 'inbound transfer seconds',
      'seg. transfer saliente': 'outbound transfer seconds',
      'segundos': 'seconds',
      'llamadas': units === 1 ? 'call' : 'calls',
      'minutos': 'minutes',
    };

    return translations[normalized] || label;
  }

  get currentTickerMessage(): string {
    return this.tickerMessages[this.tickerIndex] || '';
  }

  private refreshTicker(): void {
    this.tickerMessages = this.buildTickerMessages();
    this.tickerIndex = 0;
    this.tickerVisible = true;

    if (this.collapsed) {
      this.startTicker();
    }
  }

  private buildTickerMessages(): string[] {
    if (!this.consumption) {
      return [];
    }

    const messages: string[] = [];
    const currency = this.consumption.currency;

    // Vista parcial (member con agentes acotados): el backend no manda los
    // importes de cuenta, solo la suma de sus líneas.
    messages.push(this.consumption.partial
      ? `Total de tus agentes: ${this.formatCurrency(this.consumption.partial_lines_total, currency)}`
      : `Total del periodo: ${this.formatCurrency(this.consumption.total, currency)}`);

    const minutesPackage = this.consumption.minutes_package;
    if (minutesPackage) {
      if (minutesPackage.over_allowance) {
        messages.push(
          `Paquete de minutos superado por ${this.formatNumber(minutesPackage.consumed - minutesPackage.contracted)} min`
        );
      } else if (minutesPackage.usage_percent >= 80) {
        messages.push(`Atención: ${Math.round(minutesPackage.usage_percent)}% del paquete de minutos consumido`);
      } else {
        messages.push(`${this.formatNumber(minutesPackage.remaining)} min restantes de ${this.formatNumber(minutesPackage.contracted)}`);
      }
    }

    const balance = this.consumption.balance;
    if (balance?.kind === 'committed_tier') {
      if (balance.overage_units > 0) {
        messages.push(`Excedente del tramo: ${this.formatBalanceUnits(balance.overage_units, balance.unit)} (${this.formatCurrency(balance.overage_amount, currency)})`);
      } else {
        messages.push(`${this.formatBalanceUnits(balance.remaining_units, balance.unit)} restantes del compromiso mensual`);
      }
    } else if (balance?.kind === 'prepaid_bag') {
      messages.push(balance.available_eur != null
        ? `Bolsa prepago: ${this.formatCurrency(balance.available_eur, currency)} disponibles (≈ ${this.formatPrepaidUnits(balance.available_units, balance.unit)})`
        : `Bolsa prepago: ${this.formatBalanceUnits(balance.available_units, balance.unit)} disponibles`);
    }

    const topItem = [...this.consumption.breakdown].sort((a, b) => b.subtotal - a.subtotal)[0];
    if (topItem) {
      messages.push(`Mayor consumo: ${topItem.assistant_name} (${this.formatCurrency(topItem.subtotal, currency)})`);
    }

    if (this.historyLoaded && this.billingHistory.length >= 2) {
      const current = this.billingHistory[this.billingHistory.length - 1];
      const previous = this.billingHistory[this.billingHistory.length - 2];
      if (previous.total > 0) {
        const diffPercent = ((current.total - previous.total) / previous.total) * 100;
        const direction = diffPercent >= 0 ? 'más' : 'menos';
        messages.push(`${Math.abs(Math.round(diffPercent))}% ${direction} que el mes anterior`);
      }
    }

    return messages.length > 0 ? messages : ['Sin datos de consumo disponibles'];
  }

  private startTicker(): void {
    this.stopTicker();
    if (this.tickerMessages.length <= 1) {
      return;
    }

    this.tickerTimer = setInterval(() => {
      this.tickerVisible = false;
      this.cdr.markForCheck();
      this.tickerFadeTimer = setTimeout(() => {
        this.tickerIndex = (this.tickerIndex + 1) % this.tickerMessages.length;
        this.tickerVisible = true;
        this.cdr.markForCheck();
      }, this.TICKER_FADE_MS);
    }, this.TICKER_INTERVAL_MS);
  }

  private stopTicker(): void {
    if (this.tickerTimer) {
      clearInterval(this.tickerTimer);
      this.tickerTimer = null;
    }
    if (this.tickerFadeTimer) {
      clearTimeout(this.tickerFadeTimer);
      this.tickerFadeTimer = null;
    }
  }

  private async renderHistoryChart(): Promise<void> {
    const canvas = document.getElementById('billy-billing-history-chart') as HTMLCanvasElement | null;
    if (!canvas || this.billingHistory.length === 0) {
      return;
    }

    const { Chart, registerables } = await import('chart.js');
    Chart.register(...registerables);
    this.destroyHistoryChart();
    this.historyChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: this.billingHistory.map((month) => month.label),
        datasets: [{
          label: 'Billing',
          data: this.billingHistory.map((month) => month.total),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.14)',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          tension: 0.35,
          fill: true,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => this.formatCurrency(Number(context.parsed.y || 0), this.consumption?.currency || 'EUR')
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 6 }
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => this.formatCurrency(Number(value), this.consumption?.currency || 'EUR')
            }
          }
        }
      }
    });
  }

  private destroyHistoryChart(): void {
    if (this.historyChart) {
      this.historyChart.destroy();
      this.historyChart = null;
    }
  }

  // Sets de proyectos y agentes expandidos en el acordeón del breakdown. Las
  // claves de agente llevan el proyecto delante, porque dos proyectos pueden
  // tener agentes que se llamen igual.
  expandedProjects = new Set<string>();
  expandedAgents = new Set<string>();

  toggleProjectExpansion(projectKey: string): void {
    this.expandedProjects = this.toggled(this.expandedProjects, projectKey);
  }

  toggleAgentExpansion(agentKey: string): void {
    this.expandedAgents = this.toggled(this.expandedAgents, agentKey);
  }

  // Reassign to new Set so Angular change detection fires
  private toggled(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    return next;
  }

  // Se calcula una sola vez por respuesta (loadConsumption) en vez de en un
  // getter: como getter devolvía objetos nuevos en cada ciclo de detección de
  // cambios, y *ngFor compara por identidad, Angular destruía y reconstruía
  // todas las filas del desglose en cada click de la página.
  breakdownGroups: ProjectGroup[] = [];

  trackGroupByKey(_index: number, group: { key: string }): string {
    return group.key;
  }

  trackPeriodByKey(_index: number, period: PeriodOption): string {
    return period.key;
  }

  // Tres niveles: proyecto → agente → concepto facturable. El proyecto lo
  // manda el servidor en cada línea (`project`); si no viene — vista de admin,
  // o cuenta sin convenio de nombres — cada agente es su propio proyecto y el
  // panel queda exactamente como estaba.
  //
  // El agente se identifica por su id (el nombre solo si la línea no lo trae):
  // dos agentes con el mismo nombre no se funden. Todo agente de la vista de
  // cliente enseña siempre sus conceptos, tenga uno o varios (#649): antes el
  // acordeón solo existía si había más de un concepto y un agente con uno solo
  // se quedaba en «solo el total».
  private buildBreakdownGroups(consumption: VisibleBillyConsumption): ProjectGroup[] {
    if (!consumption.breakdown) return [];
    const isClientView = consumption.view_mode !== 'admin_clients';
    const projects = new Map<string, Map<string, { name: string; items: any[] }>>();
    for (const item of consumption.breakdown) {
      const agentName = item.assistant_name || item.assistant_id || 'Sin nombre';
      const agentKey = item.assistant_id ? `id:${item.assistant_id}` : `name:${agentName}`;
      const projectName = item.project || agentName;
      if (!projects.has(projectName)) projects.set(projectName, new Map());
      const agents = projects.get(projectName)!;
      if (!agents.has(agentKey)) agents.set(agentKey, { name: agentName, items: [] });
      agents.get(agentKey)!.items.push(item);
    }

    return Array.from(projects.entries()).map(([projectName, agentEntries]) => {
      const agents: BreakdownGroup[] = Array.from(agentEntries.entries()).map(([agentKey, agent]) => ({
        key: `${projectName}|${agentKey}`,
        name: agent.name,
        subtotal: agent.items.reduce((s, i) => s + (Number(i.subtotal) || 0), 0),
        unitsText: this.totalUnitsText(agent.items),
        lines: agent.items.map((item, index) => ({
          key: `${agentKey}|${index}`,
          label: item.unit_label || item.unit || '',
          unitsText: this.itemUnitsText(item),
          subtotal: Number(item.subtotal) || 0
        })),
        hasDetail: isClientView && agent.items.length > 0
      }));

      return {
        key: projectName,
        name: projectName,
        subtotal: agents.reduce((s, agent) => s + agent.subtotal, 0),
        unitsText: this.totalUnitsText(Array.from(agentEntries.values()).flatMap((agent) => agent.items)),
        agents,
        hasAgentLevel: agents.length > 1
      };
    });
  }

  private unitOf(item: any): UnitCode | null {
    if (item?.unit === 'seconds' || item?.unit === 'minutes' || item?.unit === 'calls') {
      return item.unit;
    }
    const byType = UNIT_BY_BILLING_TYPE[item?.billing_type];
    if (byType) return byType;
    const label = String(item?.unit_label || '').toLowerCase();
    if (/llamada|contestador|buz[oó]n/.test(label)) return 'calls';
    if (/^\s*min/.test(label)) return 'minutes';
    if (/seg/.test(label)) return 'seconds';
    if (/min/.test(label)) return 'minutes';
    return null;
  }

  // Las unidades se enseñan en la unidad con la que factura la regla (#588):
  // una regla por segundo sale en segundos («24.704 s») y una por minuto, en
  // minutos («412 min»). Así el cliente puede cuadrar unidades × precio con
  // el importe de al lado. Antes el tiempo se pasaba a una duración legible
  // («412 min» para 24.704 s facturados por segundo) y no cuadraba.
  formatBilledUnits(units: number | null | undefined, unit: UnitCode | null): string {
    const value = BillyConsumptionPanelComponent.unitsFormatter.format(Number(units) || 0);
    if (unit === 'seconds') return `${value} s`;
    if (unit === 'minutes') return `${value} min`;
    return value;
  }

  private itemUnitsText(item: any): string {
    return this.formatBilledUnits(item.units, this.unitOf(item));
  }

  // Total de la fila de un agente o proyecto: el tiempo de sus conceptos, sumado
  // por unidad. Segundos y minutos no se convierten entre sí (un minuto
  // iniciado no son 60 segundos facturados): si conviven, salen los dos
  // («24.704 s · 90 min»). Los recuentos (buzones, llamadas) no se suman al
  // tiempo; si el agente solo tiene recuentos, se suman entre sí.
  private totalUnitsText(items: any[]): string {
    const byUnit = new Map<UnitCode, number>();
    for (const item of items) {
      const unit = this.unitOf(item);
      if (unit === 'seconds' || unit === 'minutes') {
        byUnit.set(unit, (byUnit.get(unit) || 0) + (Number(item.units) || 0));
      }
    }
    if (byUnit.size > 0) {
      return (['seconds', 'minutes'] as UnitCode[])
        .filter((unit) => byUnit.has(unit))
        .map((unit) => this.formatBilledUnits(byUnit.get(unit), unit))
        .join(' · ');
    }
    if (items.length > 0 && items.every((item) => this.unitOf(item) === 'calls')) {
      const calls = items.reduce((sum, item) => sum + (Number(item.units) || 0), 0);
      return `${this.formatBilledUnits(calls, 'calls')} ${calls === 1 ? 'llamada' : 'llamadas'}`;
    }
    return '';
  }
}
